import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { usePatient } from "../../../state/patientContext";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { DiseaseMatrices } from "fe-common/utils/zoomUtil";
import { useConversationDriver } from "../../../driver/ConversationDriverProvider";
import { QualifiedFeature } from "fe-common/graphql";
import { DiseaseReasoning } from "../../../state/stateManagers/demoPaneStateManager";

//region [[ Props ]]
export interface DemoZoomGraphButtonSupport {
  onShowGraph: (qf: QualifiedFeature) => void;
}

export interface DemoZoomContent {
  setTitle?: Dispatch<SetStateAction<string>>;
  currentSelectedDisease?: DiseaseReasoning;
  onReferencesClick?: () => void;
  desktop?: boolean;
  selectedFinding?: QualifiedFeature;
  isBackgroundFinding?: boolean;
}

export enum DemoZoomViewState {
  Loading = -1,
  SuggestionReasoning,
  DiseaseReasoning,
  References,
  TriageReasoning,
  GraphView,
  GraphDetails,
}

export interface DemoReasoningControllerApi extends DemoZoomGraphButtonSupport {
  open: boolean;
  loadingMessage: string;
  onCloseClick: () => void;
  currentView: DemoZoomViewState;
  selectedDiseaseReasoning: DiseaseReasoning;
  onDiseaseInfoClick: (d: DiseaseMatrices) => void;
  onReferencesClick: () => void;
  currentSelectedDisease: DiseaseReasoning;
  onGoBack: () => void;
  allowBack: boolean;
  selectedFinding?: QualifiedFeature;
  showGraphDetails: () => void;
}

//endregion [[ Props ]]

//region [[ Functions ]]

const LoadingMessages = [
  "calling server...",
  "analyzing",
  "Matching to Kahun's Knowledge graph",
  "Gathering citations...",
  "looks like it takes longer than we thought",
  "almost there...",
];
//endregion [[ Functions ]]

/**
 * Use this hook to control over the demo reasoning window, since we have
 * two different variations of it (desktop and mobile) this controller
 * manages the show/hide, the current view state etc.
 *
 * @param props
 * @constructor
 */
export const useDemoReasoningController: () => DemoReasoningControllerApi = () => {
  const timeout = useRef(null);

  // We want to circle around the list of loading massages, so we use a state and
  // update it using useTimeout untill the loading state ends
  const [currentLoadingIndex, setCurrentLoadingIndex] = useState(0);

  // To get the reasoning we need to request the next question but with { debug:true }
  // flag in the request. Then we store the reasoning in our current state, to
  // avoid un-needed requests we want to run this request once - so we use this
  // flag to mark weather this request was initiated already and can be read from
  // the state, or not - and then needs to be requested from the server.
  const [alreadyRequested, setWasAlreadyRequested] = useState(false);

  const [currentView, setCurrentView] = useState<DemoZoomViewState>(
    DemoZoomViewState.SuggestionReasoning,
  );

  // The user can select to see a specific condition's reasoning (or graph), is so
  // this object will hold its id and (translated) name
  const [currentSelectedDisease, setCurrentSelectedDisease] = useState<
    DiseaseReasoning
  >(null);

  // We can show a graph for specific finding (in the context of a graph from
  // the suggestion to this finding, or from the selectedDisease to this finding.
  // also might be used for other scenarios where we might want to look a finiding
  // in the context of other state objects so i chose to call it "selectedFinding"
  // other than "showGraphForFinding"
  const [selectedFinding, setSelectedFinding] = useState<QualifiedFeature>(
    null,
  );

  // This is all the objects we read from the state
  const {
    selectedSuggestion,
    updateSelectedSuggestion,
    conversation,
    selectedDiseaseReasoning,
    updateSelectedDiseaseReasoning,
    setConversationDebug,
    showTriageReasoning,
    setShowTriageReasoning,
    setGraphSelection,
  } = usePatient();

  const onCloseClick = useCallback(() => {
    // Reset View
    setCurrentView(DemoZoomViewState.SuggestionReasoning);
    updateSelectedDiseaseReasoning(null);
    // Clear selected disease to close the popup
    updateSelectedSuggestion(null);
    // Clear also triage reasoning
    setShowTriageReasoning(false);
    // Initialize the current pop-up view
    setCurrentLoadingIndex(0);
  }, [
    updateSelectedDiseaseReasoning,
    updateSelectedSuggestion,
    setShowTriageReasoning,
    setCurrentView,
  ]);

  const onGoBack = useCallback(() => {
    setCurrentView((old) => {
      // Clear current selected disease if going back from disease reasoning
      if (old === DemoZoomViewState.DiseaseReasoning)
        setCurrentSelectedDisease(null);
      // If we had a disease selected for reasoning (from the result page) clear this selection
      if (currentSelectedDisease) {
        updateSelectedDiseaseReasoning(null);
      }
      // If we went back from the graph, clear it's selections
      if (old === DemoZoomViewState.GraphView) {
        setGraphSelection?.(null);
      }
      // If we went back from disease reasoning while a triage reasoning was
      // originally requested - show the triage
      if (showTriageReasoning) return DemoZoomViewState.TriageReasoning;
      // If we hit back in references and were viewing a disease, go back to the
      // disease reasoning view
      if (old === DemoZoomViewState.References && !!currentSelectedDisease)
        return DemoZoomViewState.DiseaseReasoning;
      // In all other scenarios assume the view enum is ordered correctly and just go back
      return Math.min(0, --old);
    });
  }, [
    setCurrentView,
    setCurrentSelectedDisease,
    currentSelectedDisease,
    showTriageReasoning,
    updateSelectedDiseaseReasoning,
    setGraphSelection,
  ]);

  // If we requested to see references (This happens only in mobile)
  // first notify analytics, and then update the view.
  const onReferencesClick = useCallback(() => {
    evt({
      category: CATEGORY.PATIENT_CHAT,
      action: ACTION.PATIENT_Q_REFERENCES,
      label: currentSelectedDisease?.diseaseName || "",
    });
    setCurrentView(DemoZoomViewState.References);
  }, [setCurrentView, currentSelectedDisease]);

  // If we requested to see a disease reasoning, notify analytics, update the state
  // with the selected disease id and name and update the view
  const onDiseaseInfoClick = useCallback(
    (d: DiseaseMatrices) => {
      evt({
        category: CATEGORY.PATIENT_CHAT,
        action: ACTION.PATIENT_Q_DISEASE_INFO,
        label: d.diseaseName,
      });
      setCurrentSelectedDisease(d);
      setCurrentView(DemoZoomViewState.DiseaseReasoning);
    },
    [setCurrentView],
  );

  // We need the driver to update the reasoning in the current state (managed
  // by the driver) - This is hacky and will be redesigned for the demo
  // to hold its own reasoning tate once provided from the server
  const driver = useConversationDriver();

  const getReasoning = useCallback(() => {
    setCurrentView(DemoZoomViewState.Loading);
    // Let analytics know the reasoning was requested and the condition it was
    // requested for
    evt({
      category: CATEGORY.PATIENT_CHAT,
      action: ACTION.PATIENT_Q_REASONING,
      label: selectedSuggestion?.qualifiedFeature?.feature?.name,
    });
    // Just call for next question with debug true
    driver
      .getReasoning()
      .then((reasoning) => {
        // Update the state with the reasoning data, stored in the "debug"
        // section of our state
        setConversationDebug(reasoning);
      })
      .finally(() => {
        // Avoid un-needed requests
        setWasAlreadyRequested(true);
        // Show the reasoning instead of loading
        setCurrentView(DemoZoomViewState.SuggestionReasoning);
      });
  }, [
    driver,
    setCurrentView,
    setConversationDebug,
    setWasAlreadyRequested,
    selectedSuggestion,
  ]);

  // Reset state when the selected item to view changes
  useEffect(() => {
    setWasAlreadyRequested(false);
    if (selectedDiseaseReasoning) {
      setSelectedFinding(null);
      setCurrentSelectedDisease(selectedDiseaseReasoning);
      setCurrentView(DemoZoomViewState.DiseaseReasoning);
      return;
    }
    if (showTriageReasoning) {
      setCurrentView(DemoZoomViewState.TriageReasoning);
      setCurrentSelectedDisease(null);
      setSelectedFinding(null);
      return;
    }
    setCurrentView(DemoZoomViewState.SuggestionReasoning);
    setCurrentSelectedDisease(null);
    setSelectedFinding(null);
    setGraphSelection?.(null);
  }, [
    selectedSuggestion,
    selectedDiseaseReasoning,
    setWasAlreadyRequested,
    showTriageReasoning,
    setSelectedFinding,
    setGraphSelection,
  ]);

  // Make sure we have suggestions if a question was selected
  // and if not, get the reasoning and wait for it to get here
  // make sure it happens once, if the solver didn't return
  // reasoning just show an error
  useEffect(() => {
    if (selectedSuggestion && !alreadyRequested) {
      // Make sure we get reasoning only once, use atomic setState to be on the safe side
      setWasAlreadyRequested((wasItAlreadyRequested) => {
        if (!wasItAlreadyRequested) getReasoning();
        // anyways
        return true;
      });
    }
  }, [
    selectedSuggestion,
    conversation,
    getReasoning,
    alreadyRequested,
    setWasAlreadyRequested,
  ]);

  const incCurrentLoadingMessage = useCallback(() => {
    setCurrentLoadingIndex((old) => ++old);
  }, [setCurrentLoadingIndex]);

  useEffect(() => {
    if (currentView === DemoZoomViewState.Loading) {
      timeout.current = setInterval(incCurrentLoadingMessage, 3000);
      // If we are not loading anymore, stop the timer from cycling through lading texts
    } else if (timeout.current) {
      clearInterval(timeout.current);
    }
  }, [currentView, incCurrentLoadingMessage]);

  const onShowGraph = useCallback(
    (feature: QualifiedFeature) => {
      if (currentView === DemoZoomViewState.SuggestionReasoning) {
        setCurrentSelectedDisease({
          diseaseName: feature.name,
          diseaseId: feature.id,
        });
      } else setSelectedFinding(feature);
      setCurrentView(DemoZoomViewState.GraphView);
    },
    [currentView, setCurrentView, setSelectedFinding],
  );

  const showGraphDetails = useCallback(() => {
    setCurrentView(DemoZoomViewState.GraphDetails);
  }, [setCurrentView]);

  const open =
    !!selectedSuggestion || !!selectedDiseaseReasoning || !!showTriageReasoning;

  const loadingMessage =
    LoadingMessages[currentLoadingIndex % LoadingMessages.length];

  const allowBack =
    // Always show back unless: ->
    !(
      currentView === DemoZoomViewState.SuggestionReasoning || // The view is the first reasoning view
      currentView === DemoZoomViewState.TriageReasoning || // Triage reasoning is being viewed, it's a first view without back
      (selectedDiseaseReasoning && // Or we selected disease reasoning from the results -
      !showTriageReasoning && // (but not from triage)
        // and we currently see the disease reasoning which is also the first view in this case
        currentView === DemoZoomViewState.DiseaseReasoning)
    );

  return useMemo(
    () => ({
      open,
      onCloseClick,
      loadingMessage,
      currentView,
      selectedDiseaseReasoning,
      currentSelectedDisease,
      onDiseaseInfoClick,
      onReferencesClick,
      onGoBack,
      allowBack,
      onShowGraph,
      selectedFinding,
      showGraphDetails,
    }),
    [
      open,
      onCloseClick,
      loadingMessage,
      currentView,
      selectedDiseaseReasoning,
      currentSelectedDisease,
      onDiseaseInfoClick,
      onReferencesClick,
      onGoBack,
      allowBack,
      onShowGraph,
      selectedFinding,
      showGraphDetails,
    ],
  );
};
